import { useEffect, useId, useMemo, useState } from 'react';
import { SITE_URL, getStrings, localePath, type Lang } from '../../lib/i18n';
import { matchupStrings, fmt, toolPath, TOOL_SLUGS, type MatchupStrings } from '../../lib/tools';
import { athletePath, eventPath, formatDate, headFor, displayCountry, flagFor } from '../../lib/events';
import {
  commonOpponents,
  headToHead,
  pairSlug,
  searchProfiles,
  sharedEvents,
  summarize,
  type Profile,
  type SideSummary,
} from '../../lib/matchup';
import { ARCHIVE, MANIFEST, POPULAR_PAIRS, POPULAR_PAIR_SLUGS, PROFILES, PROFILE_BY_SLUG, RANKINGS } from '../../lib/matchupData';
import { CopyButton } from './ToolParts';

export const MATCHUP_SLUG = TOOL_SLUGS.matchup;
export const matchupPairPath = (lang: Lang, pair: string) => localePath(lang, `/tools/${MATCHUP_SLUG}/${pair}`);

function Avatar({ p, size = 56 }: { p: Profile; size?: number }) {
  const [broken, setBroken] = useState(false);
  const src = headFor(p.name);
  const initials = p.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <span className="relative inline-flex shrink-0 rounded-2xl overflow-hidden bg-elevated border border-line items-center justify-center font-black text-faint" style={{ width: size, height: size }}>
      {src && !broken ? (
        <img src={src} alt="" width={size} height={size} loading="lazy" onError={() => setBroken(true)} className="w-full h-full object-cover" />
      ) : (
        initials
      )}
    </span>
  );
}

function Picker({
  label,
  value,
  exclude,
  onPick,
  t,
}: {
  label: string;
  value: Profile | null;
  exclude: string | null;
  onPick: (p: Profile | null) => void;
  t: MatchupStrings;
}) {
  const id = useId();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const results = useMemo(() => searchProfiles(PROFILES, q, 8).filter((p) => p.slug !== exclude), [q, exclude]);

  if (value) {
    return (
      <div className="card-chunky p-4 flex items-center gap-3">
        <Avatar p={value} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-black uppercase tracking-wide text-faint">{label}</p>
          <p className="font-heading font-black text-ink text-lg leading-tight truncate">{value.name}</p>
          <p className="text-sm text-muted truncate">{flagFor(value.country)} {displayCountry(value.country)}</p>
        </div>
        <button type="button" onClick={() => onPick(null)} className="btn-3d btn-3d--ghost btn-3d--sm px-3 py-1.5 print:hidden">{t.form.change}</button>
      </div>
    );
  }
  const listId = `${id}-list`;
  return (
    <div className="card-chunky p-4 relative">
      <label htmlFor={`${id}-input`} className="text-xs font-black uppercase tracking-wide text-faint">{label}</label>
      <input
        id={`${id}-input`}
        type="search"
        role="combobox"
        aria-expanded={results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={results[active] ? `${id}-opt-${active}` : undefined}
        autoComplete="off"
        placeholder={t.form.search}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
          if (e.key === 'Enter' && results[active]) { e.preventDefault(); onPick(results[active]); setQ(''); }
        }}
        className="field-chunky mt-1"
      />
      {q && (
        <ul id={listId} role="listbox" className="mt-2 max-h-72 overflow-auto rounded-2xl border-2 border-line bg-surface">
          {results.length === 0 && <li className="px-3 py-2 text-sm text-faint">{t.form.noResults}</li>}
          {results.map((p, i) => (
            <li
              key={p.slug}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(p); setQ(''); }}
              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm ${i === active ? 'bg-[var(--brand-soft)]' : 'hover:bg-elevated'}`}
            >
              <Avatar p={p} size={32} />
              <span className="font-bold text-ink">{p.name}</span>
              <span className="text-faint truncate">{flagFor(p.country)} {displayCountry(p.country)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Same formatting as the athlete pages (AthletePage.tsx).
const height = (m: { cm?: string | null; imp: string | null }) => (m.cm ? `${m.cm} cm${m.imp ? ` (${m.imp})` : ''}` : null);
const girth = (m: { cm?: string | null; imp: string | null }) => (m.cm ? `${m.cm} cm${m.imp ? ` (${m.imp}")` : ''}` : null);

function rowsFor(a: SideSummary, b: SideSummary, lang: Lang, t: MatchupStrings) {
  const L = getStrings(lang).eastVsWest.athlete.labels;
  const arm = getStrings(lang).eastVsWest.arm;
  const side = (s: SideSummary) => {
    const p = s.profile;
    return {
      country: p.country ? `${flagFor(p.country)} ${displayCountry(p.country)}`.trim() : null,
      age: p.age != null ? String(p.age) : null,
      record: p.wins != null && p.losses != null ? `${p.wins}–${p.losses}` : null,
      height: height(p.height),
      weight: p.weight.kg ? `${p.weight.kg} kg${p.weight.lbs ? ` (${p.weight.lbs} lb)` : ''}` : null,
      biceps: girth(p.biceps),
      forearm: girth(p.forearm),
      wrist: girth(p.wrist),
      evwRecord: s.evwBouts ? `${s.evwWins}–${s.evwLosses}` : null,
      armSplit: s.evwBouts ? fmt(t.table.armSplitValue, { right: s.rightArmBouts, left: s.leftArmBouts }) : null,
      evwEvents: s.evwEvents ? String(s.evwEvents) : null,
      weightClasses: s.weightClasses.length ? s.weightClasses.join(', ') : null,
      rankings: s.rankings.length
        ? s.rankings.map((r) => `${r.champion ? '🏆' : `#${r.rank}`} ${r.className} · ${arm[r.arm]}`).join('\n')
        : t.table.unranked,
    };
  };
  const A = side(a);
  const B = side(b);
  const rows: [string, string | null, string | null][] = [
    [L.country, A.country, B.country],
    [L.age, A.age, B.age],
    [L.record, A.record, B.record],
    [L.height, A.height, B.height],
    [L.weight, A.weight, B.weight],
    [L.biceps, A.biceps, B.biceps],
    [L.forearm, A.forearm, B.forearm],
    [L.wrist, A.wrist, B.wrist],
    [t.table.rankings, A.rankings, B.rankings],
    [t.table.evwRecord, A.evwRecord, B.evwRecord],
    [t.table.armSplit, A.armSplit, B.armSplit],
    [t.table.evwEvents, A.evwEvents, B.evwEvents],
    [t.table.weightClasses, A.weightClasses, B.weightClasses],
  ];
  // Only rows where the data has something for at least one side.
  return rows.filter(([, x, y]) => x || y);
}

export function Comparison({ a, b, lang, headingLevel = 'h2' }: { a: Profile; b: Profile; lang: Lang; headingLevel?: 'h2' | 'h3' }) {
  const t = matchupStrings(lang);
  const ev = getStrings(lang).eastVsWest;
  const sa = summarize(a, ARCHIVE, RANKINGS, MANIFEST);
  const sb = summarize(b, ARCHIVE, RANKINGS, MANIFEST);
  const rows = rowsFor(sa, sb, lang, t);
  const h2h = headToHead(a, b, ARCHIVE, MANIFEST);
  const shared = sharedEvents(a, b, ARCHIVE, MANIFEST);
  const common = commonOpponents(a, b, ARCHIVE, MANIFEST);
  const aw = h2h.filter((m) => m.aWon).length;
  const bw = h2h.length - aw;
  const H = headingLevel;
  const cell = 'px-3 py-2.5 align-top';
  return (
    <div>
      <div className="overflow-x-auto rounded-3xl border-2 border-line bg-surface">
        <table className="w-full text-sm">
          <caption className="sr-only">{t.table.heading}</caption>
          <thead>
            <tr className="border-b-2 border-line">
              <th scope="col" className={`${cell} text-left text-faint font-bold w-1/3`}>{t.table.stat}</th>
              {[a, b].map((p) => (
                <th key={p.slug} scope="col" className={`${cell} text-left`}>
                  <a href={athletePath(lang, p.slug)} className="font-heading font-black text-ink hover:text-[var(--brand-ink)] hover:underline">{p.name}</a>
                  {p.nickname && <span className="block text-xs text-[var(--brand-ink)]">“{p.nickname}”</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, x, y]) => (
              <tr key={label} className="border-b border-line last:border-b-0">
                <th scope="row" className={`${cell} text-left text-faint font-semibold`}>{label}</th>
                <td className={`${cell} text-ink font-semibold whitespace-pre-line`}>{x || t.table.noData}</td>
                <td className={`${cell} text-ink font-semibold whitespace-pre-line`}>{y || t.table.noData}</td>
              </tr>
            ))}
            <tr>
              <th scope="row" className={`${cell} text-left text-faint font-semibold`}>{t.table.lastEvent}</th>
              {[sa, sb].map((s) => (
                <td key={s.profile.slug} className={`${cell} font-semibold`}>
                  {s.lastEvent ? <a href={eventPath(lang, s.lastEvent.slug)} className="text-[var(--brand-ink)] hover:underline">{s.lastEvent.name}</a> : t.table.noData}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {[a, b].map((p) => (
          <a key={p.slug} href={athletePath(lang, p.slug)} className="font-bold text-[var(--brand-ink)] hover:underline">{p.name}: {t.table.profileLink} →</a>
        ))}
      </p>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <section className="card-float p-5">
          <H className="font-black text-ink mb-2">{t.h2h.heading}</H>
          {h2h.length === 0 ? (
            <p className="text-sm text-muted">{t.h2h.none}</p>
          ) : (
            <>
              <p className="text-sm font-bold text-ink mb-2">{fmt(t.h2h.tally, { a: a.name, aw, bw, b: b.name })}</p>
              <ul className="text-sm divide-y divide-line">
                {h2h.map((m, i) => (
                  <li key={i} className="py-2">
                    <span className="font-semibold text-ink">{fmt(t.h2h.won, { winner: m.winner, score: m.score.replace('-', '–') })}</span>
                    <span className="block text-xs text-faint">
                      <a href={eventPath(lang, m.eventSlug)} className="hover:underline">{m.eventName}</a> · {formatDate(m.isoDate)} · {ev.arm[m.arm]}{m.weightClass ? ` · ${m.weightClass}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
        <section className="card-float p-5">
          <H className="font-black text-ink mb-2">{t.shared.heading}</H>
          {shared.length === 0 ? (
            <p className="text-sm text-muted">{t.shared.none}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {shared.map((e) => (
                <li key={e.slug}><a href={eventPath(lang, e.slug)} className="inline-block rounded-full border-2 border-line px-3 py-1 text-sm font-bold text-ink hover:border-[var(--brand)]">{e.name}</a></li>
              ))}
            </ul>
          )}
          <H className="font-black text-ink mt-5 mb-2">{t.common.heading}</H>
          {common.length === 0 ? (
            <p className="text-sm text-muted">{t.common.none}</p>
          ) : (
            <ul className="text-sm divide-y divide-line">
              {common.slice(0, 10).map((c) => (
                <li key={c.opponent} className="py-1.5 flex justify-between gap-3">
                  <span className="text-ink font-semibold">{c.opponent}</span>
                  <span className="text-faint tabular-nums">
                    {c.a.map((w) => (w ? t.common.win : t.common.loss)).join(' ')} · {c.b.map((w) => (w ? t.common.win : t.common.loss)).join(' ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}


function FunPick({ a, b, lang }: { a: Profile; b: Profile; lang: Lang }) {
  const t = matchupStrings(lang);
  const key = `armprogress_matchup_pick:${pairSlug(a.slug, b.slug)}`;
  const [pick, setPick] = useState<string | null>(null);
  useEffect(() => {
    try {
      setPick(localStorage.getItem(key));
    } catch {
      setPick(null);
    }
  }, [key]);
  const save = (v: string | null) => {
    setPick(v);
    try {
      if (v) localStorage.setItem(key, v);
      else localStorage.removeItem(key);
    } catch {
      /* storage blocked: keep it in memory only */
    }
  };
  const picked = pick ? PROFILE_BY_SLUG.get(pick) : null;
  return (
    <section className="mt-6 rounded-3xl border-2 border-dashed border-line p-5 print:hidden" aria-labelledby="fun-pick">
      <h3 id="fun-pick" className="font-black text-ink">🎲 {t.fun.heading}</h3>
      <p className="text-sm text-muted mt-1 mb-3">{t.fun.body}</p>
      <div className="flex flex-wrap gap-2">
        {[a, b].map((p) => (
          <button key={p.slug} type="button" aria-pressed={pick === p.slug} onClick={() => save(p.slug)} className={`btn-3d btn-3d--sm px-4 py-2 ${pick === p.slug ? '' : 'btn-3d--ghost'}`}>
            {p.name}
          </button>
        ))}
        {picked && <button type="button" onClick={() => save(null)} className="text-sm font-bold text-faint hover:text-ink px-2">{t.fun.clear}</button>}
      </div>
      {picked && <p className="text-sm font-bold text-ink mt-3" aria-live="polite">{fmt(t.fun.yourPick, { name: picked.name })}</p>}
    </section>
  );
}

export function PopularMatchups({ lang, current }: { lang: Lang; current?: string }) {
  const t = matchupStrings(lang);
  return (
    <section className="mb-14" aria-labelledby="popular-matchups">
      <h2 id="popular-matchups" className="text-2xl md:text-3xl font-extrabold text-ink mb-4">{t.popular.heading}</h2>
      <ul className="grid sm:grid-cols-2 gap-2">
        {POPULAR_PAIRS.filter(([x, y]) => pairSlug(x, y) !== current).map(([x, y]) => {
          const pa = PROFILE_BY_SLUG.get(x)!;
          const pb = PROFILE_BY_SLUG.get(y)!;
          return (
            <li key={pairSlug(x, y)}>
              <a href={matchupPairPath(lang, pairSlug(x, y))} className="card-chunky is-hoverable flex items-center gap-2 px-4 py-3 text-sm font-bold text-ink">
                {pa.name} <span className="text-[var(--brand-ink)]">vs</span> {pb.name}
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function Matchup({ lang, initial, embed = false }: { lang: Lang; initial?: [string, string]; embed?: boolean }) {
  const t = matchupStrings(lang);
  const fallback = initial ?? POPULAR_PAIRS[0];
  const [aSlug, setA] = useState<string | null>(fallback[0]);
  const [bSlug, setB] = useState<string | null>(fallback[1]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const qa = q.get('a');
    const qb = q.get('b');
    if (qa && PROFILE_BY_SLUG.has(qa)) setA(qa);
    if (qb && PROFILE_BY_SLUG.has(qb) && qb !== qa) setB(qb);
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const p = new URLSearchParams();
    if (aSlug) p.set('a', aSlug);
    if (bSlug) p.set('b', bSlug);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${p.toString() ? `?${p}` : ''}`);
  }, [aSlug, bSlug, dirty]);

  const a = aSlug ? PROFILE_BY_SLUG.get(aSlug) ?? null : null;
  const b = bSlug ? PROFILE_BY_SLUG.get(bSlug) ?? null : null;
  const shareUrl = () => {
    if (!a || !b) return `${SITE_URL}${toolPath(lang, MATCHUP_SLUG)}`;
    const ps = pairSlug(a.slug, b.slug);
    return POPULAR_PAIR_SLUGS.includes(ps)
      ? `${SITE_URL}${matchupPairPath(lang, ps)}`
      : `${SITE_URL}${toolPath(lang, MATCHUP_SLUG)}?a=${a.slug}&b=${b.slug}`;
  };

  return (
    <div>
      <div className={`grid gap-3 ${embed ? 'sm:grid-cols-[1fr_auto_1fr]' : 'md:grid-cols-[1fr_auto_1fr]'} items-center mb-4`}>
        <Picker label={t.form.athleteA} value={a} exclude={bSlug} onPick={(p) => { setA(p?.slug ?? null); setDirty(true); }} t={t} />
        <button
          type="button"
          onClick={() => { setA(bSlug); setB(aSlug); setDirty(true); }}
          aria-label={t.form.swap}
          title={t.form.swap}
          className="btn-3d btn-3d--ghost w-11 h-11 p-0 mx-auto font-black print:hidden"
        >
          ⇄
        </button>
        <Picker label={t.form.athleteB} value={b} exclude={aSlug} onPick={(p) => { setB(p?.slug ?? null); setDirty(true); }} t={t} />
      </div>

      {a && b ? (
        <>
          <div className="flex flex-wrap gap-2 mb-4 print:hidden">
            <CopyButton getText={shareUrl} label={`🔗 ${t.actions.copyLink}`} copiedLabel={t.actions.copied} className="btn-3d btn-3d--sm px-4 py-2" />
          </div>
          <Comparison a={a} b={b} lang={lang} />
          <FunPick a={a} b={b} lang={lang} />
        </>
      ) : (
        <p className="card-float p-6 text-muted text-center">{t.form.pickTwo}</p>
      )}
      <p className="text-xs text-faint mt-6 leading-relaxed">{t.disclaimer}</p>
    </div>
  );
}
